// Prefetch helper: preloads route chunks on hover/touch/focus to improve perceived navigation speed
// Keep this file dependency-free and tolerant to missing window/DOM in tests

type Prefetchers = Record<string, () => Promise<any>>;

const routeMap: Record<string, () => Promise<any>> = {
    '/': () => import('../components/Home/HomeScreen'),
    '/login': () => import('../pages/Auth/LoginPage'),
    '/signup': () => import('../pages/Auth/SignUpPage'),
    '/history': () => import('../pages/Static/ClubHistory'),
    '/members': () => import('../pages/Members/MembersHub'),
    '/members/hub-v2': () => import('../pages/Members/MembersHubV2'),
    '/feed': () => import('../pages/Feed/ActivityFeed'),
    '/feed-v2': () => import('../pages/Feed/ActivityFeedV2'),
    '/messages': () => import('../pages/Messages/Inbox'),
    '/events': () => import('../components/Events/EventsSummary'),
    '/events/': () => import('../pages/Events/EventDetails'),
    '/gallery': () => import('../pages/Members/GalleryListPage'),
    '/gallery/': () => import('../pages/Members/GalleryDetailPage'),
    '/matches/': () => import('../pages/Members/MatchDetailPage'),
    '/directory': () => import('../pages/Members/Directory'),
    '/admin': () => import('../pages/Admin/AdminDashboardPage'),
    '/admin/events': () => import('../pages/Admin/AdminEventsPage'),
    '/admin/members': () => import('../pages/Admin/AdminMembersPage'),
    '/admin/venues': () => import('../pages/Admin/AdminVenuesPage'),
    '/admin/bostons': () => import('../pages/Admin/AdminBostonsPage'),
    '/admin/leaderboard': () => import('../pages/Admin/AdminLeaderboardPage'),
    '/admin/matches': () => import('../pages/Admin/AdminMatchesListPage'),
    '/admin/tournaments': () => import('../pages/Admin/AdminTournamentsPage'),
    '/admin/checkin': () => import('../pages/Admin/AdminCheckInPage'),
    '/admin/system': () => import('../pages/Admin/AdminSystemPage'),
    '/admin/reports': () => import('../pages/Admin/AdminReportsPage'),
    '/admin/bot-flags': () => import('../pages/Admin/AdminBotFlagsPage'),
    '/feedback': () => import('../pages/Static/AppFeedback'),
    '/profile': () => import('../pages/Members/PublicProfile'),
    '/settings': () => import('../pages/Members/SettingsPage'),
    '/arena': () => import('../arena/pages/ArenaLobbyPage'),
    '/leaderboard': () => import('../pages/Members/LeaderboardPage'),
    '/points-ledger': () => import('../pages/Members/PointsLedger')
};

const prefetched = new Set<string>();

// Config
const HOVER_DELAY = 160; // ms before honoring a hover
const THROTTLE_MS = 300; // min time between prefetches
const IDLE_TIMEOUT = 1500; // requestIdleCallback timeout

let hoverTimer: number | null = null;
let lastPrefetch = 0;

function shouldPrefetch() {
  if (typeof navigator === 'undefined') return false;
  const anyNav = (navigator as any);
  if (anyNav.connection) {
    if (anyNav.connection.saveData) return false;
    const effective = anyNav.connection.effectiveType || '';
    if (/2g|slow-2g/i.test(effective)) return false;
  }
  return true;
}

function scheduleImport(fn: () => Promise<any>, immediate = false) {
  try {
    if (!shouldPrefetch()) return;
    const now = Date.now();
    if (!immediate && (now - lastPrefetch < THROTTLE_MS)) return;
    lastPrefetch = now;
    const run = () => fn().catch(() => {});
    if (immediate) {
      run();
    } else if (typeof (window as any).requestIdleCallback === 'function') {
      (window as any).requestIdleCallback(() => run(), { timeout: IDLE_TIMEOUT });
    } else {
      setTimeout(() => run(), 50);
    }
  } catch (_) {}
}

export function prefetchRoute(pathname: string, immediate = false) {
  const keys = Object.keys(routeMap).sort((a, b) => b.length - a.length);
  for (const key of keys) {
    if (key === '/' ? pathname === '/' : pathname.startsWith(key)) {
      if (prefetched.has(key)) return;
      prefetched.add(key);
      scheduleImport(routeMap[key], immediate);
      return;
    }
  }
}

export function startHoverPrefetch() {
  if (typeof document === 'undefined') return;
  const onHover = (e: Event) => {
    try {
      const target = e.target as HTMLElement | null;
      const anchor = target && (target.closest ? (target.closest('a') as HTMLAnchorElement | null) : null);
      if (!anchor || !anchor.href) return;
      const url = new URL(anchor.href, window.location.href);
      if (url.origin !== window.location.origin) return;

      if (e.type === 'touchstart') {
        // likely navigating, prefetch immediately
        prefetchRoute(url.pathname, true);
        return;
      }

      // hover/focus: delay slightly to avoid accidental hovers
      if (hoverTimer) {
        clearTimeout(hoverTimer);
        hoverTimer = null;
      }
      hoverTimer = window.setTimeout(() => {
        prefetchRoute(url.pathname);
        hoverTimer = null;
      }, HOVER_DELAY);
    } catch (_) {}
  };

  document.addEventListener('mouseover', onHover, { passive: true });
  document.addEventListener('touchstart', onHover, { passive: true });
  document.addEventListener('focusin', onHover, { passive: true });

  return () => {
    if (hoverTimer) {
      clearTimeout(hoverTimer);
      hoverTimer = null;
    }
    document.removeEventListener('mouseover', onHover);
    document.removeEventListener('touchstart', onHover);
    document.removeEventListener('focusin', onHover);
  };
}

export default { prefetchRoute, startHoverPrefetch };